
import React, { useState } from 'react';
import { format } from 'date-fns';
import { PaymentStatusChange } from '@/types/communication';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Wallet, AlertTriangle, Loader2 } from 'lucide-react';

interface PaymentStatusHistoryProps {
  history: PaymentStatusChange[];
  isLoading?: boolean;
  error?: string | null;
}

const PaymentStatusHistory: React.FC<PaymentStatusHistoryProps> = ({ 
  history, 
  isLoading = false,
  error = null
}) => {
  const [isTableExpanded, setIsTableExpanded] = useState(true);
  
  const toggleTable = () => {
    setIsTableExpanded(!isTableExpanded);
  };
  
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">Loading payment history...</p>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-8 text-destructive">
          <AlertTriangle className="h-8 w-8 mb-2" />
          <p className="font-medium">Failed to load payment history</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      );
    }
    
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Previous Status</TableHead>
            <TableHead>New Status</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Notes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center">
                No payment status changes recorded
              </TableCell>
            </TableRow>
          ) : (
            history.map((change) => (
              <TableRow key={change.id}>
                <TableCell>{format(new Date(change.changedAt), 'PPP')}</TableCell>
                <TableCell>
                  <Badge variant={change.previousStatus === 'paid' ? 'default' : 'secondary'}>
                    {change.previousStatus}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={change.newStatus === 'paid' ? 'default' : 'secondary'}>
                    {change.newStatus}
                  </Badge>
                </TableCell>
                <TableCell>{change.amount} AFN</TableCell>
                <TableCell>{change.notes || '-'}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    );
  };
  
  return (
    <Card>
      <CardHeader className="cursor-pointer" onClick={toggleTable}>
        <CardTitle className="flex items-center">
          <Wallet className="h-5 w-5 mr-2" />
          Payment Status Changes
          <Badge variant="outline" className="ml-2">
            {history.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isTableExpanded && renderContent()}
      </CardContent>
    </Card>
  );
};

export default PaymentStatusHistory;
